/**
 * Color conversion between the Flutter `Color(int)` hex format
 * (`"0xFFA7CAFF"` — AARRGGBB) used on the appointment-profile wire
 * and the CSS `#RRGGBB` format consumed by the React display layer.
 *
 * Wire shape verified against live dev cURL 2026-05-15. Backend ships:
 *   "color": "0xFFA7CAFF"   // alpha=FF, R=A7, G=CA, B=FF
 *
 * Display layer needs:
 *   "#A7CAFF"               // CSS hex
 *
 * IMPORTANT — the wire format is load-bearing for the Flutter apps.
 * Flutter renders the appointment-card colour with
 * `Color(int.tryParse(appointmentProfile.color)!)`; a CSS `#RRGGBB`
 * value makes `int.tryParse` return null, the `!` throws during build,
 * and (with no custom ErrorWidget) the card renders as a gray box and
 * the appointment disappears. Any colour written to `appointmentProfile.color`
 * MUST be run through `cssColorToFlutter` first. Lives in shared/ so both
 * the scheduling and settings domains can normalise on their write paths.
 */

/**
 * Parse a Flutter `Color(int)` hex string and return the CSS hex.
 *
 * Examples:
 *   "0xFFA7CAFF"   → "#A7CAFF"
 *   "0x00ffffff"   → "#FFFFFF" (alpha discarded for opaque CSS)
 *   "#A7CAFF"      → "#A7CAFF" (idempotent — already CSS form)
 *   null / ""      → null     (caller falls back to a default color)
 *
 * Falls back to `null` for unrecognised input rather than throwing —
 * the appointment-profile list display tolerates `null` by showing
 * a neutral chip.
 */
export function flutterColorToCss(value: string | null | undefined): string | null {
  if (!value) return null;

  // Already CSS hex — short-circuit.
  if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
    return value.toUpperCase();
  }
  // CSS 8-digit (with alpha) — strip alpha.
  if (/^#[0-9A-Fa-f]{8}$/.test(value)) {
    return '#' + value.slice(3).toUpperCase();
  }

  // Flutter `0xAARRGGBB` form.
  const match = /^0x([0-9A-Fa-f]{8})$/.exec(value);
  if (match && match[1]) {
    // Skip first 2 hex digits (alpha), take RRGGBB.
    return '#' + match[1].slice(2).toUpperCase();
  }

  // Flutter `0xRRGGBB` form (rare — no alpha).
  const matchShort = /^0x([0-9A-Fa-f]{6})$/.exec(value);
  if (matchShort && matchShort[1]) {
    return '#' + matchShort[1].toUpperCase();
  }

  return null;
}

/**
 * Convert a CSS hex to the Flutter `0xAARRGGBB` form (opaque alpha).
 * Use on every write that persists a colour to `appointmentProfile.color`.
 *
 *   "#A7CAFF"     → "0xFFA7CAFF"
 *   "#a7caff"     → "0xFFA7CAFF" (uppercased)
 *   "0xFFA7CAFF"  → "0xFFA7CAFF" (idempotent — already Flutter form)
 *
 * Returns `null` for unrecognised input.
 */
export function cssColorToFlutter(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^#([0-9A-Fa-f]{6})$/.exec(value);
  if (match && match[1]) {
    return '0xFF' + match[1].toUpperCase();
  }
  // Already Flutter form — idempotent.
  if (/^0x[0-9A-Fa-f]{8}$/.test(value)) {
    return value.toUpperCase().replace('0X', '0x');
  }
  return null;
}
